import { supabase } from '../lib/supabase';

export const demoLeadService = {
  // Get all demo leads with optional filters
  async getAllLeads(filters = {}) {
    try {
      let query = supabase?.from('demo_leads')?.select('*')?.order('signup_date', { ascending: false });

      // Apply status filter
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('demo_access_status', filters?.status);
      }

      // Apply conversion filter
      if (filters?.converted !== undefined) {
        query = query?.eq('converted_to_customer', filters?.converted);
      }

      // Apply date range filter
      if (filters?.startDate) {
        query = query?.gte('signup_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('signup_date', filters?.endDate);
      }

      // Apply search filter
      if (filters?.search) {
        query = query?.or(`email.ilike.%${filters?.search}%,name.ilike.%${filters?.search}%,academy_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(lead => ({
        id: lead?.id,
        email: lead?.email,
        name: lead?.name,
        academyName: lead?.academy_name,
        signupDate: lead?.signup_date,
        demoAccessStatus: lead?.demo_access_status,
        demoActivatedAt: lead?.demo_activated_at,
        demoExpiresAt: lead?.demo_expires_at,
        convertedToCustomer: lead?.converted_to_customer,
        convertedAt: lead?.converted_at,
        organizationId: lead?.organization_id,
        engagementScore: lead?.engagement_score,
        lastActivityAt: lead?.last_activity_at,
        notes: lead?.notes,
        createdAt: lead?.created_at,
        updatedAt: lead?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching demo leads:', error);
      throw error;
    }
  },

  // Get single lead by ID
  async getLeadById(leadId) {
    try {
      const { data, error } = await supabase?.from('demo_leads')?.select('*')?.eq('id', leadId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        email: data?.email,
        name: data?.name,
        academyName: data?.academy_name,
        signupDate: data?.signup_date,
        demoAccessStatus: data?.demo_access_status,
        demoActivatedAt: data?.demo_activated_at,
        demoExpiresAt: data?.demo_expires_at,
        convertedToCustomer: data?.converted_to_customer,
        convertedAt: data?.converted_at,
        organizationId: data?.organization_id,
        engagementScore: data?.engagement_score,
        lastActivityAt: data?.last_activity_at,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching lead:', error);
      throw error;
    }
  },

  // Update lead status
  async updateLeadStatus(leadId, status) {
    try {
      const { data, error } = await supabase?.from('demo_leads')?.update({ demo_access_status: status })?.eq('id', leadId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        demoAccessStatus: data?.demo_access_status,
        demoActivatedAt: data?.demo_activated_at,
        demoExpiresAt: data?.demo_expires_at
      };
    } catch (error) {
      console.error('Error updating lead status:', error);
      throw error;
    }
  },

  // Mark lead as converted
  async markAsConverted(leadId, organizationId = null) {
    try {
      const { data, error } = await supabase?.from('demo_leads')?.update({
          converted_to_customer: true,
          converted_at: new Date()?.toISOString(),
          demo_access_status: 'converted',
          organization_id: organizationId
        })?.eq('id', leadId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        convertedToCustomer: data?.converted_to_customer,
        convertedAt: data?.converted_at,
        demoAccessStatus: data?.demo_access_status
      };
    } catch (error) {
      console.error('Error marking lead as converted:', error);
      throw error;
    }
  },

  // Update lead notes
  async updateNotes(leadId, notes) {
    try {
      const { data, error } = await supabase?.from('demo_leads')?.update({ notes })?.eq('id', leadId)?.select()?.single();

      if (error) throw error;

      return { id: data?.id, notes: data?.notes };
    } catch (error) {
      console.error('Error updating notes:', error);
      throw error;
    }
  },

  // Get dashboard statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('demo_leads')?.select('demo_access_status, converted_to_customer');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(l => l?.demo_access_status === 'pending')?.length || 0,
        active: data?.filter(l => l?.demo_access_status === 'active')?.length || 0,
        expired: data?.filter(l => l?.demo_access_status === 'expired')?.length || 0,
        converted: data?.filter(l => l?.converted_to_customer === true)?.length || 0,
        conversionRate: data?.length > 0 
          ? ((data?.filter(l => l?.converted_to_customer === true)?.length / data?.length) * 100)?.toFixed(1)
          : '0.0'
      };

      return stats;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
};
function createDemoLead(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createDemoLead is not implemented yet.', args);
  return null;
}

export { createDemoLead };